import { Component, computed, input, output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { GenerateAudioPayload, AzureVoiceSystem } from '@mira-avant/mtlibs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { TextareaField } from '../../../fields/setup-wizard/textarea-field/textarea-field';
import { AzureVoicesDropdownField } from '../../../fields/setup-wizard/azure-voices-dropdown-field/azure-voices-dropdown-field';
import { DecimalField } from '../../../fields/setup-wizard/decimal-field/decimal-field';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';

enum SsmlMode {
	OFF = 'off',
	ON = 'on',
}

type AudioFormType = {
	voice: FormControl<string | null>;
	prosody: FormControl<number | null>;
	text: FormControl<string | null>;
};

const DEFAULT_FORM_VALUES = {
	voice: null,
	prosody: 1.0,
	text: null,
};

@Component({
	selector: 'app-audio-form',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		NzButtonModule,
		NzFormModule,
		NzInputModule,
		NzSpinModule,
		TextareaField,
		DecimalField,
		AzureVoicesDropdownField,
		NzRadioModule,
	],
	standalone: true,
	templateUrl: './audio-form.html',
	styleUrl: './audio-form.scss',
})
export class AudioForm {
	public loadingSingle = input<boolean>(false);
	public loadingBatch = input<boolean>(false);
	public loading = computed(() => this.loadingSingle() || this.loadingBatch());
	public onClear = output<void>();
	public onSubmitSingle = output<GenerateAudioPayload>();
	public onSubmitBatch = output<GenerateAudioPayload>();
	public Validators = Validators;
	public ssmlMode = SsmlMode.OFF;
	private sub?: Subscription;
	public SsmlMode = SsmlMode;

	public form = new FormGroup<AudioFormType>({
		voice: new FormControl<string | null>(DEFAULT_FORM_VALUES.voice, {
			nonNullable: false,
			validators: [Validators.required],
		}),
		prosody: new FormControl<number | null>(DEFAULT_FORM_VALUES.prosody, {
			nonNullable: false,
			validators: [Validators.required],
		}),
		text: new FormControl<string | null>(DEFAULT_FORM_VALUES.text, {
			nonNullable: false,
			validators: [Validators.required],
		}),
	});

	constructor() {
		//watch for changes to the voice dropdown and prosody field and if ssmlMode is on, apply the SSML template
		this.sub = this.form.get('voice')!.valueChanges.subscribe(voice => {
			if (this.ssmlMode === SsmlMode.ON) {
				this.applySsmlTemplate();
			}
		});
		this.sub.add(
			this.form.get('prosody')!.valueChanges.subscribe(prosody => {
				if (this.ssmlMode === SsmlMode.ON) {
					this.applySsmlTemplate();
				}
			})
		);
	}

	onSsmlModeChange(value: SsmlMode) {
		//clear any existing value in the text field in the form
		this.form.get('text')?.setValue(null);

		if (value === SsmlMode.ON) {
			this.applySsmlTemplate();
		}
	}

	private applySsmlTemplate() {
		//Get the currently selected voice from the form
		const voice = this.form.get('voice')?.value;

		if (!voice) {
			return;
		}

		//Apply the SSML template based on the selected voice and prosody
		const prosody = this.form.get('prosody')?.value ?? 1.0;

		const ssml = `<speak version="1.0"
  xmlns="http://www.w3.org/2001/10/synthesis"
  xmlns:mstts="https://www.w3.org/2001/mstts"
  xml:lang="en-US">

    <voice name="${voice}">
      <prosody rate="${prosody}">
        [put word or sentence here]
      </prosody>
  </voice>
</speak>`;

		this.form.get('text')?.setValue(ssml);
	}

	onSubmitSingleClick() {
		if (this.form.valid) {
			const value = this.form.getRawValue();

			if (!value['voice'] || !value['prosody'] || !value['text']) {
				throw new Error('Missing values');
			}

			this.onSubmitSingle.emit({
				voiceSystem: AzureVoiceSystem.Neural,
				voice: value['voice'],
				prosody: value['prosody'],
				text: value['text'],
			});
		} else {
			this.form.markAllAsTouched();
		}
	}

	onSubmitBatchClick() {
		if (this.form.valid) {
			const value = this.form.getRawValue();

			if (!value['voice'] || !value['prosody'] || !value['text']) {
				throw new Error('Missing values');
			}

			this.onSubmitBatch.emit({
				voiceSystem: AzureVoiceSystem.Neural,
				voice: value['voice'],
				prosody: value['prosody'],
				text: value['text'],
			});
		} else {
			this.form.markAllAsTouched();
		}
	}

	onClearClicked() {
		this.form.reset(DEFAULT_FORM_VALUES);

		this.onClear.emit();
	}

	ngOnDestroy() {
		this.sub?.unsubscribe();
	}
}
